import React from 'react';

interface Props { fallback?: React.ReactNode; children: React.ReactNode; }
interface State { hasError: boolean; error?: Error; }

export class ErrorBoundary extends React.Component<Props, State> {
  state: State = { hasError: false };

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  render() {
    if (this.state.hasError) {
      return this.props.fallback ?? (
        <div role="alert" className="flex flex-col items-center justify-center gap-4 p-8 text-center">
          <span className="text-4xl" aria-hidden>⚠️</span>
          <h2 className="text-lg font-semibold text-neutral-900">Something went wrong</h2>
          <p className="text-sm text-neutral-500">{this.state.error?.message}</p>
          <button
            onClick={() => this.setState({ hasError: false })}
            className="px-4 py-2 rounded-md bg-blue-500 text-white text-sm hover:bg-blue-600"
          >Try again</button>
        </div>
      );
    }
    return this.props.children;
  }
}

export const EmptyState: React.FC<{ icon?: string; title: string; body?: string; cta?: React.ReactNode }> = (
  { icon = '📭', title, body, cta }
) => (
  <div className="flex flex-col items-center justify-center gap-3 py-16 text-center">
    <span className="text-4xl" aria-hidden>{icon}</span>
    <h3 className="font-semibold text-neutral-800">{title}</h3>
    {body && <p className="text-sm text-neutral-500 max-w-xs">{body}</p>}
    {cta && <div className="mt-2">{cta}</div>}
  </div>
);

export const ErrorState: React.FC<{ message?: string; onRetry?: () => void }> = (
  { message = 'Something went wrong. Please try again.', onRetry }
) => (
  <div role="alert" className="flex flex-col items-center gap-3 py-16 text-center">
    <span className="text-4xl" aria-hidden>🚨</span>
    <p className="text-sm text-neutral-600">{message}</p>
    {onRetry && (
      <button onClick={onRetry} className="px-4 py-2 rounded-md bg-neutral-900 text-white text-sm hover:bg-neutral-700">
        Retry
      </button>
    )}
  </div>
);

export const PageLoader: React.FC = () => (
  <div className="flex items-center justify-center h-full min-h-[60vh]" role="status" aria-label="Loading">
    <span className="h-10 w-10 animate-spin rounded-full border-4 border-blue-500 border-t-transparent" />
  </div>
);
